import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, ConfidenceMeter, Empty, ErrorNotice, Loading, Modal, Notice,
  fmt, fmtDate, useToast,
} from '../components/ui.jsx';

export default function InsurersPage() {
  const { can } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const query = useQuery({
    queryKey: ['insurers', includeInactive],
    queryFn: () => api.listInsurers(includeInactive).then((r) => r.data),
  });

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Insurers' }]} />
      <div className="page-head">
        <div>
          <h1>Insurance companies</h1>
          <div className="page-sub">
            Registering an insurer takes effect immediately. Insurer codes are stored as rows, not columns,
            so onboarding a new company needs no schema change and no release.
          </div>
        </div>
        <div className="spacer" />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive
        </label>
        {can('admin') && <button className="btn btn-primary" onClick={() => setAddOpen(true)}>Register insurer</button>}
      </div>

      <Card bodyClass="tight">
        {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
          : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
            : (query.data || []).length === 0 ? <Empty title="No insurers registered" />
              : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Insurer</th>
                        <th>Code column in their file</th>
                        <th className="num">Records</th>
                        <th className="num">Mapped</th>
                        <th className="num">Pending</th>
                        <th style={{ minWidth: 150 }}>Coverage</th>
                        <th>Last import</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(query.data || []).map((i) => {
                        const pct = i.recordCount ? (i.mappedCount / i.recordCount) * 100 : 0;
                        return (
                          <tr key={i.id}>
                            <td>
                              <div className="cell-main">{i.name}</div>
                              <div className="cell-sub">{i.shortCode}{i.irdaiCode ? ` · IRDAI ${i.irdaiCode}` : ''}</div>
                            </td>
                            <td className="prov">{i.codeColumn || 'not recorded'}</td>
                            <td className="num">{fmt(i.recordCount)}</td>
                            <td className="num">{fmt(i.mappedCount)}</td>
                            <td className="num">{fmt(i.pendingCount)}</td>
                            <td>
                              {i.recordCount
                                ? <ConfidenceMeter value={pct} band={pct >= 90 ? 'high' : pct >= 60 ? 'medium' : 'low'} />
                                : <span className="prov">no master imported</span>}
                            </td>
                            <td>{fmtDate(i.lastImportAt)}</td>
                            <td><Badge kind={i.isActive ? 'approved' : 'superseded'}>{i.isActive ? 'active' : 'inactive'}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
      </Card>

      {addOpen && <AddInsurerModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AddInsurerModal({ onClose }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ shortCode: '', name: '', irdaiCode: '', codeColumn: '' });
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mutation = useMutation({
    mutationFn: () => api.createInsurer({
      shortCode: form.shortCode.trim().toUpperCase(),
      name: form.name.trim(),
      irdaiCode: form.irdaiCode.trim() || null,
      codeColumn: form.codeColumn.trim() || null,
    }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`${data.name} registered as ${data.shortCode}`);
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
      onClose();
    },
    onError: setError,
  });

  const valid = form.shortCode.trim().length >= 2 && form.name.trim().length >= 3;

  return (
    <Modal
      title="Register an insurance company"
      onClose={onClose}
      footer={(
        <>
          <button className="btn btn-primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving...' : 'Register insurer'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <ErrorNotice error={error} />

      <div className="grid-2">
        <div className="field">
          <label htmlFor="i-code">Short code *</label>
          <input
            id="i-code" value={form.shortCode} onChange={set('shortCode')}
            placeholder="SBI" maxLength={16} style={{ textTransform: 'uppercase' }}
          />
          <div className="field-hint">
            2 to 16 characters, letters digits and underscore. Used in the API and on every screen, so it
            cannot be changed later.
          </div>
        </div>
        <div className="field">
          <label htmlFor="i-name">Registered name *</label>
          <input id="i-name" value={form.name} onChange={set('name')} placeholder="SBI General Insurance" />
        </div>
        <div className="field">
          <label htmlFor="i-irdai">IRDAI registration number</label>
          <input id="i-irdai" value={form.irdaiCode} onChange={set('irdaiCode')} />
        </div>
        <div className="field">
          <label htmlFor="i-column">Vehicle code column in their master</label>
          <input id="i-column" value={form.codeColumn} onChange={set('codeColumn')} placeholder="sbi_vehicle_master_id" />
          <div className="field-hint">
            Recorded for provenance, and used to pre-fill the column mapping on the next upload. You can
            still override it per file.
          </div>
        </div>
      </div>

      <Notice tone="info" title="After registering">
        The insurer appears immediately in the upload wizard and in every filter.
        Go to <Link to="/upload">Upload master</Link> to load their file and run the matching engine.
      </Notice>
    </Modal>
  );
}
